import React, {useState} from 'react';
import {convertDurationsToMonths} from '../utils/durationUtils';
import RangeSlider from './RangeSlider';
import {formatCurrency} from '../utils/currencyUtils';
import './LoanForm.css';

const LoanForm = ({
                      productData,
                      amountSelected,
                      durationSelected,
                      currency,
                      installmentDetails,
                      handleAmountChange,
                      handleDurationChange,
                      onSubmit,
                      onSecciRequest,
                      getValidDurations,
                      labels,
                      isLoading,
                      onlyApplyButton,
                      applyButtonId,
                      hideDuration,
                      creditAccountLabel,
                      fullDisclosureMode,
                      amountTouched,
                      durationTouched,
                      costBreakdownEnabled,
                      aprLabel,
                      interestLabel,
                      commissionLabel,
                      costDetailsToggleLabel,
                      totalLoanAmountLabel,
                      totalCostLabel,
                      apr,
                      aprc,
                      interest,
                      commission,
                      totalCost,
                  }) => {
    const [costDetailsExpanded, setCostDetailsExpanded] = useState(false);
    if (!productData) return null;

    const durations = isLoading
        ? [1, 3, 6, 9, 12]
        : convertDurationsToMonths(getValidDurations());

    if (onlyApplyButton) {
        return (
            <form onSubmit={onSubmit} className={`loan-form ${isLoading ? 'loading-state' : ''} only-apply-button`}>
                <div className="button-container">
                    <button
                        type="submit"
                        className={`apply-button ${isLoading ? 'disabled' : ''}`}
                        id={applyButtonId}
                        disabled={isLoading}
                    >
                        {labels.submitLabel || "Apply Now"}
                    </button>
                </div>
            </form>
        );
    }

    const decimals = labels.numDecimalMonthly ?? 0;
    const hasSelection = fullDisclosureMode ? (amountTouched && durationTouched) : true;
    const monthly = installmentDetails.installmentAmount || 0;
    const totalFromApi = installmentDetails.totalAmount;
    const totalAmount = hasSelection
        ? (totalFromApi != null ? totalFromApi : monthly * durationSelected)
        : 0;
    const formattedMonthly = hasSelection
        ? formatCurrency(monthly, decimals)
        : formatCurrency(0, decimals);
    const formattedTotal = hasSelection
        ? formatCurrency(totalAmount, decimals)
        : formatCurrency(0, decimals);
    const formattedAmountValue = fullDisclosureMode && !amountTouched
        ? formatCurrency(0, 0, currency)
        : formatCurrency(installmentDetails.principal, 0, currency);
    const formattedDurationValue = fullDisclosureMode && !durationTouched
        ? `0 ${labels.monthsLabel || ''}`
        : `${durationSelected} ${labels.monthsLabel || ''}`;

    const cbFormattedTotal = hasSelection ? formatCurrency(totalAmount, 2, currency) : formatCurrency(0, 2, currency);
    const cbFormattedAprc = aprc != null && aprc !== '' ? aprc : '0.00%';
    const cbFormattedApr = apr != null && apr !== '' ? formatCurrency(apr, 2, currency) : formatCurrency(0, 2, currency);
    const cbFormattedInterest = hasSelection ? formatCurrency(interest, 2, currency) : formatCurrency(0, 2, currency);
    const cbFormattedCommission = hasSelection ? formatCurrency(commission, 2, currency) : formatCurrency(0, 2, currency);
    const cbFormattedPrincipal = hasSelection ? formatCurrency(installmentDetails.principal, 2, currency) : formatCurrency(0, 2, currency);
    const cbFormattedTotalCost = hasSelection ? formatCurrency(totalCost, 2, currency) : formatCurrency(0, 2, currency);
    const resolvedInterestLabel = (interestLabel || '').replace('{rate}', cbFormattedApr);
    const withCurrency = (value) => (
        <>
            {currency === "£" && <span className="cb-unit">{currency}</span>}
            {value}
            {currency !== "£" && <span className="cb-unit"> {currency}</span>}
        </>
    );

    return (
        <form onSubmit={onSubmit} className={`loan-form ${isLoading ? 'loading-state' : ''} ${fullDisclosureMode ? 'full-disclosure-mode' : ''} ${costBreakdownEnabled ? 'cost-breakdown-mode' : ''}`}>
            <div className="slider-container">
                {labels.loanAmountLabel && (
                    <div className="label-container center">
                        <h2>{labels.loanAmountLabel}</h2>
                    </div>
                )}
                <div className="label-container">
                    {currency === "£" ? (
                        <p className="priceclass">
                            <span>{currency}</span>
                            {formattedAmountValue}
                            <span className='label-credit-account'>{creditAccountLabel}</span>
                        </p>
                    ) : (
                        <p className="priceclass">
                            {formattedAmountValue}
                            <span>{currency}</span>
                            <span className='label-credit-account'>{creditAccountLabel}</span>
                        </p>
                    )}
                </div>
                {!costBreakdownEnabled && (
                    <div className="label-container monthly-row">
                        <span className="label-text">
                            {labels.monthlyLabel || "Loan Duration (Months)"}
                        </span>

                                        <span className="value-text">
                            {currency === "£" && <span>{currency}</span>}
                                            {formattedMonthly}
                                            {currency !== "£" && <span> {currency}</span>}
                        </span>
                    </div>
                )}

                <div className="sliders">
                    <RangeSlider
                        values={productData.amountRange}
                        value={amountSelected}
                        onChange={handleAmountChange}
                        hideFromTo={true}
                        hideMinMax={true}
                        disabled={isLoading}
                    />

                    {!hideDuration && (
                        <>
                            <div className="label-container">
            <span className="label-text">
                {labels.durationLabel || "Loan Duration (Months)"}
            </span>
                                <span className="value-text">
                {formattedDurationValue}
            </span>
                            </div>

                            <RangeSlider
                                values={durations}
                                value={durationSelected}
                                onChange={handleDurationChange}
                                hideFromTo={true}
                                hideMinMax={true}
                                disabled={isLoading}
                            />
                        </>
                    )}

                </div>

                {fullDisclosureMode && !costBreakdownEnabled && (
                    <div className="label-container total-amount-row">
                        <span className="label-text">
                            {labels.totalAmountLabel || 'Total amount payable'}
                        </span>
                        <span className="value-text">
                            {currency === "£" && <span>{currency}</span>}
                            {formattedTotal}
                            {currency !== "£" && <span> {currency}</span>}
                        </span>
                    </div>
                )}

                {costBreakdownEnabled && (
                    <div className="cost-breakdown-card">
                        <div className="cost-breakdown-row cost-breakdown-headline-row">
                            <div className="cost-breakdown-item">
                                <span className="cb-label">{labels.monthlyLabel || "Monthly Installment"}</span>
                                <span className="cb-value cb-value-large">{withCurrency(formattedMonthly)}</span>
                            </div>
                            <div className="cost-breakdown-item cost-breakdown-item-right">
                                <span className="cb-label">{aprLabel}</span>
                                <span className="cb-value cb-value-large">{cbFormattedAprc}</span>
                            </div>
                        </div>

                        <div className="cost-breakdown-divider"/>

                        <div className="cost-breakdown-row">
                            <span className="cb-label">{labels.totalAmountLabel || 'Total amount payable'}</span>
                            <span className="cb-value">{withCurrency(cbFormattedTotal)}</span>
                        </div>
                    </div>
                )}

                {costBreakdownEnabled && (
                    <div className="cost-details-card">
                        <button
                            type="button"
                            className={`cost-details-toggle ${costDetailsExpanded ? 'expanded' : ''}`}
                            aria-expanded={costDetailsExpanded}
                            onClick={() => setCostDetailsExpanded((prev) => !prev)}
                        >
                            <span>{costDetailsToggleLabel}</span>
                            <span className="cost-details-chevron" aria-hidden="true"/>
                        </button>

                        {costDetailsExpanded && (
                            <div className="cost-details-panel">
                                <div className="cost-breakdown-row">
                                    <span className="cb-label">{totalLoanAmountLabel}</span>
                                    <span className="cb-value">{withCurrency(cbFormattedPrincipal)}</span>
                                </div>
                                <div className="cost-breakdown-row">
                                    <span className="cb-label">{commissionLabel}</span>
                                    <span className="cb-value">{withCurrency(cbFormattedCommission)}</span>
                                </div>
                                <div className="cost-breakdown-row">
                                    <span className="cb-label">{resolvedInterestLabel}</span>
                                    <span className="cb-value">{withCurrency(cbFormattedInterest)}</span>
                                </div>
                                <div className="cost-breakdown-row">
                                    <span className="cb-label">{totalCostLabel}</span>
                                    <span className="cb-value">{withCurrency(cbFormattedTotalCost)}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {labels.infoLinkLabel && (
                    <div className="label-container">
                        <a
                            href="#"
                            className={`secci-button ${isLoading ? 'disabled' : ''} ${costBreakdownEnabled ? 'prominent' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                if (!isLoading) onSecciRequest();
                            }}
                        >
                            {labels.infoLinkLabel || "View SECCI Document"}
                        </a>
                    </div>
                )}
            </div>

            <div className="button-container">

                <button
                    type="submit"
                    className={`apply-button ${isLoading ? 'disabled' : ''}`}
                    id={applyButtonId}
                    disabled={isLoading || (fullDisclosureMode && !hasSelection)}
                >
                    {labels.submitLabel || "Apply Now"}
                </button>
            </div>
        </form>
    );
};

export default LoanForm;
